/**
 * App.jsx — Root Application Component
 *
 * URL-based routing via React Router. Each view has its own shareable URL;
 * the browser back/forward buttons work naturally.
 *
 * ┌──────────────────────────────────────────────────────────────────┐
 * │  Route map:                                                       │
 * │                                                                  │
 * │  /                          → Homepage (browse)                  │
 * │  /store                     → Ceramic pieces shop                │
 * │  /faq                       → Full FAQ page                      │
 * │  /classes/studio-hand-building  → Hand Building gallery          │
 * │  /classes/studio-wheel-throwing → Wheel Throwing gallery         │
 * │  /booking/:cardId           → Firebase booking flow              │
 * │  /b/:slug                   → Shareable alias → /booking/:cardId │
 * │  *                          → Redirect to /                      │
 * └──────────────────────────────────────────────────────────────────┘
 *
 * Scroll management:
 *   Navigating away saves the current scroll position. Navigating back
 *   to "/" restores it so the user lands where they left off.
 *
 */
import { useRef, useEffect, lazy, Suspense } from 'react';
import {
  Routes,
  Route,
  Navigate,
  useParams,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { CLASSES } from './data/classes';
import Nav from './components/Nav';
import Hero from './components/Hero';
import StudioIntro from './components/StudioIntro';
import ClassCarousel from './components/ClassCarousel';
import ClassGrid from './components/ClassGrid';
import FAQTile from './components/FAQ';
import FAQPage from './components/FAQPage';
import About from './components/About';
import ShopTile from './components/ShopTile';
import ShopPage from './components/ShopPage';
import Newsletter from './components/Newsletter';
import Gallery from './components/Gallery';
import Footer from './components/Footer';
import FirebaseBookingPage from './components/FirebaseBookingPage';
import BookingBySlug from './components/BookingBySlug';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsAndConditions from './components/TermsAndConditions';
import { useHomepageContent } from './services/firestore';
// Lazy-load admin — Firebase SDK only initializes when /admin is visited
const AdminLayout = lazy(() => import('./components/admin/AdminLayout'));

// ── Route sub-components ──────────────────────────────────────────────────────
// Defined outside App so they don't re-mount on every render.

function GalleryRoute({ onReset, onViewClasses }) {
  const { slug } = useParams();
  const sel = CLASSES.find((c) => c.gallerySlug === slug);
  if (!sel) return <Navigate to="/" replace />;
  return <Gallery sel={sel} onReset={onReset} onViewClasses={onViewClasses} />;
}

// ── Main App ──────────────────────────────────────────────────────────────────

export default function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const { content } = useHomepageContent();

  // Apply custom background texture from Firestore (if set)
  useEffect(() => {
    if (content.textureImageUrl) {
      document.body.style.setProperty(
        '--texture-url',
        `url('${content.textureImageUrl}')`,
      );
    } else {
      document.body.style.removeProperty('--texture-url');
    }
  }, [content.textureImageUrl]);

  // Saved scroll position so navigating back to "/" restores where the user was
  const savedScrollY = useRef(0);
  const restoreScroll = useRef(false);

  // Disable browser-native scroll restoration so refreshes always start at top
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  // ── Scroll management on route change ──────────────────────────────────────
  useEffect(() => {
    if (location.pathname === '/') {
      if (location.state?.scrollTo === 'classes') {
        // Clear the state so a page refresh doesn't re-scroll to classes
        window.history.replaceState({}, '');
        setTimeout(() => {
          document
            .getElementById('all-classes-section')
            ?.scrollIntoView({ behavior: 'smooth' });
        }, 50);
      } else if (restoreScroll.current) {
        window.scrollTo({ top: savedScrollY.current, behavior: 'instant' });
        restoreScroll.current = false;
      } else {
        window.scrollTo({ top: 0, behavior: 'instant' });
      }
    } else {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [location.pathname, location.state]);

  // ── Navigation helpers ──────────────────────────────────────────────────────

  function saveScroll() {
    savedScrollY.current = window.scrollY;
    restoreScroll.current = true;
  }

  function book(c) {
    saveScroll();
    // Pass the card object as route state so FirebaseBookingPage can skip the
    // Firestore fetch — the card is already in memory from ClassGrid's snapshot.
    navigate(`/booking/${c.slug || c.id}`, { state: { card: c } });
  }

  function gallery(c) {
    saveScroll();
    navigate(`/classes/${c.gallerySlug}`);
  }

  function reset() {
    navigate('/');
  }

  function openFAQ() {
    saveScroll();
    navigate('/faq');
  }

  function openShop() {
    saveScroll();
    navigate('/store');
  }

  function viewClasses() {
    navigate('/');
    setTimeout(() => {
      document
        .getElementById('all-classes-section')
        ?.scrollIntoView({ behavior: 'smooth' });
    }, 50);
  }

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div
      style={{
        fontFamily: "'Georgia',serif",
        background: '#F9F2E9',
        color: '#1a1208',
        minHeight: '100vh',
      }}
    >
      {/* Fixed navigation bar — always visible */}
      <Nav
        onReset={reset}
        onOpenShop={openShop}
        onViewClasses={viewClasses}
        onOpenFAQ={openFAQ}
      />

      {/* Main content area — offset by nav height (74px) */}
      <div style={{ paddingTop: 74 }}>
        <Routes>
          {/* ── Homepage ─────────────────────────────────────────────── */}
          <Route
            path="/"
            element={
              <>
                <Hero
                  onExplore={() =>
                    document
                      .getElementById('all-classes-section')
                      ?.scrollIntoView({ behavior: 'smooth' })
                  }
                />
                <StudioIntro />
                <div style={{ height: 80 }} />
                <ClassCarousel
                  onGallery={gallery}
                  onExplore={() =>
                    document
                      .getElementById('all-classes-section')
                      ?.scrollIntoView({ behavior: 'smooth' })
                  }
                />
                <ClassGrid onBook={book} />
                {/* FAQ freezes in place while the About tile rises over it,
                    then the page resumes at the Collection section.
                    Outer wrapper height = sticky FAQ (100vh−72) + About budget (200vh). */}
                <div style={{ position: 'relative' }}>
                  {/* Sticky FAQ — stays pinned while About tile animates */}
                  <div
                    style={{
                      position: 'sticky',
                      top: 72,
                      height: 'calc(100vh - 72px)',
                      zIndex: 10,
                      background: '#F9F2E9',
                    }}
                  >
                    <FAQTile onOpenFAQ={openFAQ} />
                  </div>
                  {/* About tile rises over the frozen FAQ */}
                  <About />
                </div>
                <div style={{ height: 80 }} />
                <ShopTile onOpenShop={openShop} />
                <Newsletter />
              </>
            }
          />

          {/* ── FAQ ──────────────────────────────────────────────────── */}
          <Route path="/faq" element={<FAQPage onReset={reset} />} />

          {/* ── Store ────────────────────────────────────────────────── */}
          <Route path="/store" element={<ShopPage onReset={reset} />} />

          {/* ── Class gallery (carousel sessions) ────────────────────── */}
          {/* /classes/studio-hand-building                               */}
          {/* /classes/studio-wheel-throwing                              */}
          <Route
            path="/classes/:slug"
            element={
              <GalleryRoute onReset={reset} onViewClasses={viewClasses} />
            }
          />

          {/* ── Firebase booking flow ────────────────────────────────────── */}
          {/* /booking/:slug — session picker → customer info → confirm      */}
          <Route path="/booking/:slug" element={<FirebaseBookingPage />} />

          {/* ── Human-readable booking alias ─────────────────────────────── */}
          {/* /b/:slug — resolves slug → redirects to /booking/:cardId        */}
          {/* Share this URL for private classes: yoursite.com/b/wheel-throwing-private */}
          <Route path="/b/:slug" element={<BookingBySlug />} />

          {/* ── Admin dashboard (lazy-loaded — Firebase only loads here) ── */}
          <Route
            path="/admin"
            element={
              <Suspense
                fallback={
                  <div style={{ padding: 48, textAlign: 'center' }}>
                    Loading...
                  </div>
                }
              >
                <AdminLayout />
              </Suspense>
            }
          />

          {/* ── Legal pages ──────────────────────────────────────────── */}
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsAndConditions />} />

          {/* ── Catch-all → homepage ─────────────────────────────────── */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>

      {/* Footer — always visible */}
      <Footer />
    </div>
  );
}
